import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, IconButton, Tooltip, Chip
} from '@mui/material';
import { Refresh, Edit, Percent } from '@mui/icons-material';
import axios from 'axios';

const API = 'https://api.bharatdarshan.app/api';

const SanataniCommission = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [totals, setTotals] = useState({});
  const [editPartner, setEditPartner] = useState(null);
  const [commissionRate, setCommissionRate] = useState('');
  const [snack, setSnack] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sanatani-hotel/admin/commission-summary`);
      setPartners(r.data?.data || []);
      setTotals(r.data?.totals || {});
    } catch (e) {
      setSnack({ msg: 'Failed to load data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (p) => { setEditPartner(p); setCommissionRate(String(p.commissionRate || 15)); };
  const closeEdit = () => { setEditPartner(null); setCommissionRate(''); };

  const saveCommission = async () => {
    const rate = parseFloat(commissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      setSnack({ msg: 'Enter a valid rate (0-100)', type: 'error' });
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/sanatani-hotel/admin/partner/${editPartner._id}/commission`, { commissionRate: rate });
      setSnack({ msg: 'Commission rate updated', type: 'success' });
      closeEdit();
      load();
    } catch (e) {
      setSnack({ msg: e.response?.data?.message || 'Failed to update', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#795548">
          Sanatani Sadan — Partner Commission
        </Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined" sx={{ borderColor: '#795548', color: '#795548' }}>
          Refresh
        </Button>
      </Box>

      {snack && (
        <Alert severity={snack.type} sx={{ mb: 2 }} onClose={() => setSnack(null)}>{snack.msg}</Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Revenue', value: fmt(totals.totalRevenue), color: '#4CAF50' },
          { label: 'Total Commission', value: fmt(totals.totalCommission), color: '#795548' },
          { label: 'Net Payable', value: fmt(totals.netPayable), color: '#2196F3' },
          { label: 'Total Withdrawn', value: fmt(totals.totalWithdrawn), color: '#FF9800' },
          { label: 'Available Payout', value: fmt(totals.availablePayout), color: '#9C27B0' },
        ].map((s) => (
          <Grid item xs={12} sm={6} md={2.4} key={s.label}>
            <Card sx={{ borderTop: `4px solid ${s.color}` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Partners Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, background: 'linear-gradient(135deg, #795548, #a1887f)', color: '#fff' }}>
          <Typography variant="h6" fontWeight={600}>Partner Commission Rates</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Set individual commission % for each Sanatani Sadan partner</Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  <TableCell><b>Partner</b></TableCell>
                  <TableCell><b>Mobile</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell align="right"><b>Commission %</b></TableCell>
                  <TableCell align="right"><b>Total Revenue</b></TableCell>
                  <TableCell align="right"><b>Net Payable</b></TableCell>
                  <TableCell align="right"><b>Withdrawn</b></TableCell>
                  <TableCell align="right"><b>Available</b></TableCell>
                  <TableCell align="center"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center">No partners found</TableCell></TableRow>
                )}
                {partners.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.businessName}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.ownerName}</Typography>
                    </TableCell>
                    <TableCell>{p.mobile}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.isApproved ? 'Approved' : 'Pending'}
                        color={p.isApproved ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={<Percent sx={{ fontSize: 14 }} />}
                        label={`${p.commissionRate}%`}
                        sx={{ background: '#795548', color: '#fff', fontWeight: 700 }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{fmt(p.totalRevenue)}</TableCell>
                    <TableCell align="right">{fmt(p.netPayable)}</TableCell>
                    <TableCell align="right">{fmt(p.totalWithdrawn)}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={p.availablePayout > 0 ? '#4CAF50' : 'text.secondary'}>
                        {fmt(p.availablePayout)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit Commission">
                        <IconButton size="small" onClick={() => openEdit(p)} sx={{ color: '#795548' }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Commission Dialog */}
      <Dialog open={!!editPartner} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #795548, #a1887f)', color: '#fff' }}>
          Edit Commission Rate
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editPartner && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Partner: <b>{editPartner.businessName}</b> ({editPartner.ownerName})
              </Typography>
              <TextField
                label="Commission Rate (%)"
                type="number"
                fullWidth
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                helperText="Enter percentage (0-100). Deducted from each booking."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={busy}>Cancel</Button>
          <Button onClick={saveCommission} disabled={busy} variant="contained"
            sx={{ background: '#795548', '&:hover': { background: '#5d4037' } }}>
            {busy ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniCommission;
